import React, { useState, useMemo } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Search, Building2, User, FileText,
  ShoppingCart, ExternalLink, Calendar, Tag, CheckCircle2,
  Clock, Truck, Package, AlertCircle, Trash2, Edit, Send
} from 'lucide-react';
import { useSearchParams, useLocation } from 'react-router-dom';
import StatusBadge from '@/components/ui/StatusBadge';
import EmptyState from '@/components/ui/EmptyState';
import PageHeader from '@/components/ui/PageHeader';
import SkeletonCard from '@/components/ui/SkeletonCard';
import { downloadOrderPDF } from '@/components/orders/OrderPDFGenerator';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import {
  releaseStock,
  reserveOrderItems,
  StockApiError,
  stockErrorToGerman,
} from '@/lib/stockApi';
import { downloadRechnung } from '@/components/orders/RechnungPDF';
import { upsertCustomerPricesFromItems } from '@/lib/customerPriceApi';
import { useAuth } from '@/api/AuthContext';
import { isAngebot, isBestellung } from '@/lib/orderFields';
import { markMirrorPaid, cancelMirror, mirrorInBackground } from '@/lib/orderMirror';
import { pushOrderInBackground, isInShopify } from '@/lib/shopifyOrderPush';
import { ensureInvoice } from '@/lib/rechnungVersand';
import { useEigeneZustellung } from '@/lib/useEigeneZustellung';
import { istEigeneZustellung } from '@/lib/versandArt';
import { fetchPdfSettings } from '@/lib/pdfRenderer';
import { leseMindermengeRegel, mitZuschlag, ohneZuschlag, isZuschlagZeile, summen } from '@/lib/mindermenge';
import { getOrderItems } from '@/lib/orderFields';
import { completeLieferungTasksForLead } from '@/components/tasks/TaskStateMachine';
import { notifyPdfCreated } from '@/components/notifications/notificationHelper';

// Status display config for Bestellungen
const ORDER_STATUS_CONFIG = {
  'Neu': { label: 'Neu', color: 'bg-blue-100 text-blue-700 border border-blue-200', icon: Clock },
  'Bestätigt': { label: 'Bestätigt', color: 'bg-indigo-100 text-indigo-700 border border-indigo-200', icon: CheckCircle2 },
  'In Bearbeitung': { label: 'In Bearbeitung', color: 'bg-amber-100 text-amber-700 border border-amber-200', icon: Package },
  'Versendet': { label: 'Versendet', color: 'bg-purple-100 text-purple-700 border border-purple-200', icon: Truck },
  'Geliefert': { label: 'Geliefert', color: 'bg-emerald-100 text-emerald-700 border border-emerald-200', icon: CheckCircle2 },
  'Abgeschlossen': { label: 'Abgeschlossen', color: 'bg-slate-100 text-slate-600 border border-slate-200', icon: CheckCircle2 },
  'Storniert': { label: 'Storniert', color: 'bg-red-100 text-red-700 border border-red-200', icon: AlertCircle },
};

function OrderStatusBadge({ status }) {
  const cfg = ORDER_STATUS_CONFIG[status] || { label: status, color: 'bg-slate-100 text-slate-600', icon: Clock };
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}>
      <Icon className="w-3 h-3" />
      {cfg.label}
    </span>
  );
}

export default function AdminOrders() {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [agentFilter, setAgentFilter] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [editingItems, setEditingItems] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const queryClient = useQueryClient();
  const eigeneZustellung = useEigeneZustellung();
  const { user } = useAuth();

  React.useEffect(() => {
    if (selectedOrder) {
      setEditingItems(selectedOrder.items || []);
      setIsEditing(false);
    }
  }, [selectedOrder]);

  const liveSubtotal = useMemo(() => {
    const items = isEditing ? editingItems : (selectedOrder?.items || []);
    return items.reduce((acc, item) => acc + ((item.quantity || 0) * (item.unit_price || 0)), 0);
  }, [editingItems, isEditing, selectedOrder]);

  const liveTaxRate = selectedOrder?.tax_rate || 19;
  const liveTaxAmount = liveSubtotal * (liveTaxRate / 100);
  const liveTotal = liveSubtotal + liveTaxAmount;

  const { data: customerPrices = [] } = useQuery({
    queryKey: ['customer-prices', selectedOrder?.lead_id],
    queryFn: () => base44.entities.CustomerPrice.filter({ lead_id: selectedOrder.lead_id }),
    enabled: !!selectedOrder?.lead_id,
  });

  const customerPriceMap = useMemo(() => {
    const map = {};
    customerPrices.forEach(cp => { map[cp.product_id] = cp.custom_price ?? cp.preis; });
    return map;
  }, [customerPrices]);

  // ── Mutations ────────────────────────────────────────────────
  const updateOrderMutation = useMutation({
    mutationFn: async ({ id, data }) => base44.entities.Order.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-all-orders']);
      toast.success('Auftrag aktualisiert');
      setSelectedOrder(null);
    },
    onError: (err) => toast.error('Fehler: ' + err.message),
  });

  // Abschließen: sadece Berechnet statüsündeki sipariş ödeme sonrası
  // kapatılır. paid_at timestamp'i set eder.
  const completeOrderMutation = useMutation({
    mutationFn: async (orderId) => {
      const now = new Date().toISOString();
      const updated = await base44.entities.Order.update(orderId, {
        status: 'Abgeschlossen',
        paid_at: now,
        updated_date: now,
      });
      // Açık "Lieferung" To-Do'su otomatik kapanır (Ergebnis bildirimde)
      completeLieferungTasksForLead({
        leadId: updated.lead_id,
        resultText: `Bestellung ${updated.order_number || ''} abgeschlossen (Admin)`,
        actorEmail: user?.email,
      }).catch(() => {});
      // P5: bezahlte Bestellung im Shopify-Spiegel als paid markieren
      mirrorInBackground(() => markMirrorPaid(updated), 'paid');
      return updated;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-all-orders']);
      queryClient.invalidateQueries(['agent-tasks']);
      queryClient.invalidateQueries(['admin-tasks']);
      toast.success('Bestellung abgeschlossen — Zahlung verbucht ✓');
      setSelectedOrder(null);
    },
    onError: (err) => toast.error('Fehler: ' + err.message),
  });

  // Rechnung erstellen: Geliefert → Berechnet geçişi.
  //   - invoice_number üretilir (RE-YYYY-NNNN)
  //   - invoiced_at + invoiced_by set
  //   - Rechnung PDF indirilir
  // Geriye uyum: Abgeschlossen olmuş eski siparişler için de admin
  // "Rechnung erstellen" tıklayabilir — bu durumda statü değişmez,
  // sadece invoice metadata eklenir.
  const createInvoiceMutation = useMutation({
    mutationFn: async (order) => {
      // Festschreiben: Nummer + Buchung + offener Posten in EINER DB-Transaktion
      const finalized = await ensureInvoice(order, user?.email);
      let updated = finalized;
      // Retroaktif fatura değilse statüyü Berechnet'e çek.
      if (order.status === 'Geliefert') {
        updated = { ...finalized, ...(await base44.entities.Order.update(order.id, {
          status: 'Berechnet',
          updated_date: new Date().toISOString(),
        })) };
      }

      try {
        await downloadRechnung({ ...updated, invoice_number: finalized.invoice_number, _invoice: finalized._invoice });
      } catch (err) {
        console.warn('[AdminOrders] Rechnung PDF failed:', err);
        toast.warning('Rechnung erstellt — aber PDF konnte nicht erzeugt werden.');
      }

      return updated;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-all-orders']);
      queryClient.invalidateQueries({ queryKey: ['fibu-invoices'] });
      toast.success('Rechnung erstellt und gebucht');
    },
    onError: (err) => toast.error('Fehler: ' + err.message),
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status }) => {
      // Sipariş "Storniert" durumuna çekildiğinde rezerve edilen stokları
      // serbest bırak. release_stock idempotent olduğu için zaten serbest
      // olan bir order'a tekrar çağrılırsa boş döner.
      if (status === 'Storniert') {
        try {
          await releaseStock({ orderId: id, actorEmail: user?.email });
        } catch (err) {
          // Release hatası statü değişikliğini engellesin — aksi halde
          // stok invariant'ı bozulur.
          if (err instanceof StockApiError) {
            throw new Error(stockErrorToGerman(err));
          }
          throw err;
        }
      }
      const updated = await base44.entities.Order.update(id, { status });
      // P5: FC-Storno → Shopify-Spiegel stornieren (fire-and-forget)
      if (status === 'Storniert' && updated?.shopify_mirror_id) {
        mirrorInBackground(() => cancelMirror(updated), 'cancel');
      }
      return updated;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-all-orders']);
      queryClient.invalidateQueries(['products']);
      toast.success('Status aktualisiert');
    },
    onError: (err) => toast.error('Fehler: ' + err.message),
  });

  const deleteOrderMutation = useMutation({
    mutationFn: async (orderId) => {
      // Silmeden önce rezervleri serbest bırak (sipariş artık bulunmayacak).
      try {
        await releaseStock({ orderId, actorEmail: user?.email });
      } catch (err) {
        // Release hatası silmeyi engellemesin: stock_movements zaten
        // yazıldığı için history kalır. Ama uyarı gösterelim.
        console.warn('release_stock on delete failed:', err);
      }
      return base44.entities.Order.delete(orderId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-all-orders']);
      queryClient.invalidateQueries(['products']);
      toast.success('Eintrag gelöscht');
      setSelectedOrder(null);
    },
    onError: (err) => toast.error('Fehler beim Löschen: ' + err.message),
  });

  const handleApproveAndDownload = async () => {
    if (!selectedOrder) return;
    // ONAY = yalnızca teklifin içeriğini onaylar. Angebot ANGEBOT KALIR:
    // depoya düşmez, stok rezerve edilmez, siparişe dönüşmez. Siparişe
    // dönüşüm ayrı ve bilinçli bir adımdır — "In Bestellung umwandeln"
    // butonu (convertToBestellungMutation).
    const updatedData = {
      items: editingItems,
      subtotal: liveSubtotal,
      tax_amount: liveTaxAmount,
      total: liveTotal,
      order_status: 'approved',
    };
    // Yazma öncesi validasyon — negatif veya geçersiz item reddet
    const invalid = editingItems.find((item) => {
      const q = Number(item.quantity);
      const p = Number(item.unit_price);
      return !Number.isFinite(q) || q <= 0 || !Number.isFinite(p) || p < 0;
    });
    if (invalid) {
      toast.error('Ungültige Position: Menge muss > 0 und Preis ≥ 0 sein.');
      return;
    }

    const priceSaveWarnings = [];
    try {
      await updateOrderMutation.mutateAsync({ id: selectedOrder.id, data: updatedData });

      // Auto-save custom prices to CustomerPrice manager
      if (editingItems && editingItems.length > 0 && selectedOrder.lead_id) {
        const { errors } = await upsertCustomerPricesFromItems({
          leadId: selectedOrder.lead_id,
          leadName: selectedOrder.lead_name || '',
          items: editingItems,
        });
        queryClient.invalidateQueries(['customer-prices']);
        if (errors.length > 0) {
          priceSaveWarnings.push(errors[0].message || 'Kundenpreise konnten nicht gespeichert werden');
        }
      }

      const tempOrder = { ...selectedOrder, ...updatedData };
      await downloadOrderPDF(tempOrder);
      if (priceSaveWarnings.length > 0) {
        toast.warning('Angebot genehmigt & PDF erstellt — Kundenpreise konnten nicht gespeichert werden.');
      } else {
        toast.success('Angebot genehmigt und PDF heruntergeladen');
      }
      if (selectedOrder.agent_email) notifyPdfCreated(selectedOrder, selectedOrder.agent_email);
    } catch (error) {
      toast.error('Fehler beim Genehmigen: ' + (error?.message || 'unbekannt'));
    }
  };

  // ──────────────────────────────────────────────────────────────
  // Angebot → Bestellung dönüşümü: BİLİNÇLİ ve TEK yol.
  // order_type 'Bestellung' olur, lifecycle 'Neu' başlar, stok rezerve
  // edilir → sipariş Warenausgang kuyruğuna düşer. Angebot kaydı silinmez,
  // aynı kayıt dönüşür (belge geçmişi ve numara korunur).
  // ──────────────────────────────────────────────────────────────
  const convertToBestellungMutation = useMutation({
    mutationFn: async (order) => {
      if ((order.order_type || '').toLowerCase() !== 'angebot') {
        throw new Error('Nur Angebote können umgewandelt werden.');
      }
      const now = new Date().toISOString();
      // Mindestbestellwert-Regel erst bei der Bestellung anwenden (Angebot bleibt ohne Zuschlag)
      const baseItems = getOrderItems(order);
      let kundenrabattProzent = 0;
      try {
        const leadRow = order.lead_id ? await base44.entities.Lead.get(order.lead_id) : null;
        kundenrabattProzent = Number(leadRow?.kundenrabatt_prozent) || 0;
      } catch { /* ohne Kontakt: Rabatt 0 */ }
      const regel = leseMindermengeRegel(await fetchPdfSettings().catch(() => null));
      const items = mitZuschlag(baseItems, regel, { kundenrabattProzent });
      const zuschlagNeu = items.some(isZuschlagZeile) && !baseItems.some(isZuschlagZeile);
      const sums = summen(items);
      const updated = await base44.entities.Order.update(order.id, {
        order_type: 'Bestellung',
        status: 'Neu',
        updated_date: now,
        ...(zuschlagNeu ? { items, positionen: items, subtotal: sums.subtotal, tax_amount: sums.tax_amount, total: sums.total } : {}),
      });
      if (zuschlagNeu) toast.info(`Mindermengenzuschlag ${regel.zuschlag.toLocaleString('de-DE', { minimumFractionDigits: 2 })} € netto angefügt (Warenwert unter Mindestbestellwert).`);

      // Stok rezervasyonu — agent Bestellung akışıyla aynı kural
      try {
        await reserveOrderItems({
          items: items.map((it) => ({
            productId: it.product_id,
            variantValue: it.variant_value ?? it.variant ?? null,
            quantity: Number(it.quantity) || 0,
          })),
          orderId: order.id,
          actorEmail: user?.email,
          allowNegative: true,
        });
      } catch (reserveErr) {
        toast.warning(
          'Reservierung fehlgeschlagen: ' +
          (reserveErr instanceof StockApiError ? stockErrorToGerman(reserveErr) : reserveErr.message)
        );
      }
      pushOrderInBackground(order.id, {
        orderNumber: order.order_number,
        onDone: () => queryClient.invalidateQueries(['admin-all-orders']),
      });
      return updated;
    },
    onSuccess: (updated) => {
      queryClient.invalidateQueries(['admin-all-orders']);
      // DB trigger'ı kontağı otomatik 'Kunde' yapar — lead cache tazele
      queryClient.invalidateQueries(['admin-leads']);
      queryClient.invalidateQueries(['leads']);
      setSelectedOrder(null);
      toast.success('Angebot in Bestellung umgewandelt — jetzt im Warenausgang sichtbar.');
    },
    onError: (err) => toast.error('Fehler: ' + err.message),
  });

  // Mindermengenzuschlag erlassen — nur Admin, nur vor der Rechnung; Grund wird in die Notizen geschrieben.
  const erlasseZuschlagMutation = useMutation({
    mutationFn: async (order) => {
      const items = ohneZuschlag(getOrderItems(order));
      const sums = summen(items);
      const stamp = `Mindermengenzuschlag erlassen (${user?.email || 'Admin'}, ${format(new Date(), 'dd.MM.yyyy HH:mm', { locale: de })})`;
      return base44.entities.Order.update(order.id, {
        items, positionen: items,
        subtotal: sums.subtotal, tax_amount: sums.tax_amount, total: sums.total,
        notes: [order.notes, stamp].filter(Boolean).join('\n'),
        updated_date: new Date().toISOString(),
      });
    },
    onSuccess: (updated) => {
      queryClient.invalidateQueries(['admin-all-orders']);
      setSelectedOrder((prev) => (prev && prev.id === updated?.id ? { ...prev, ...updated } : prev));
      toast.success('Mindermengenzuschlag erlassen.');
    },
    onError: (err) => toast.error('Fehler: ' + err.message),
  });
  const kannZuschlagErlassen = (o) => o && isBestellung(o) && !o.invoice_number
    && !['Berechnet', 'Abgeschlossen', 'Storniert'].includes(o.status)
    && getOrderItems(o).some(isZuschlagZeile);

  const handleItemChange = (index, field, value) => {
    const newItems = [...editingItems];
    const num = parseFloat(value) || 0;
    newItems[index] = { ...newItems[index], [field]: num };
    newItems[index].total = (newItems[index].quantity || 0) * (newItems[index].unit_price || 0);
    setEditingItems(newItems);
  };

  // ── Data ─────────────────────────────────────────────────────
  const { data: orders = [], isLoading } = useQuery({
    queryKey: ['admin-all-orders'],
    queryFn: () => base44.entities.Order.list('-created_date'),
    staleTime: 1000 * 30,
  });

  // Agentennamen für den Filter (E-Mail → Name)
  const { data: agentList = [] } = useQuery({
    queryKey: ['admin-agents-for-orders'],
    queryFn: () => base44.entities.Agent.list('name'),
    staleTime: 1000 * 60 * 5,
  });

  // Derin bağlantı 1 (ÖNCELİKLİ): Dashboard'dan navigate state ile gelen sipariş
  // nesnesini ANINDA aç — liste yüklenmesini beklemez, ara ekran flaşı olmaz.
  const location = useLocation();
  React.useEffect(() => {
    const stateOrder = location.state?.openOrder;
    if (stateOrder && !selectedOrder) {
      setSelectedOrder(stateOrder);
      // History state'i temizle ki geri/yenilede tekrar açılmasın
      window.history.replaceState({}, document.title);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // Derin bağlantı 2 (yedek): ?order=<id> query param (dış linkler için).
  const [searchParams, setSearchParams] = useSearchParams();
  React.useEffect(() => {
    const orderId = searchParams.get('order');
    if (orderId && orders.length > 0 && !selectedOrder) {
      const match = orders.find((o) => o.id === orderId);
      if (match) {
        setSelectedOrder(match);
        searchParams.delete('order');
        setSearchParams(searchParams, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders, searchParams]);

  // Angebot/Bestellung ayrımı merkezi helper ile: eski kayıtlarda
  // order_type eksik/farklı yazımlı olabilir — numara ön eki de sayılır.
  const bestellungen = orders.filter(isBestellung);
  const angebote = orders.filter(isAngebot);
  const agentNameByEmail = useMemo(() => {
    const map = {};
    agentList.forEach(a => { if (a.email) map[a.email.toLowerCase()] = a.name || a.email; });
    return map;
  }, [agentList]);
  const agents = [...new Set(orders.map(o => (o.agent_email || '').toLowerCase()).filter(Boolean))]
    .sort((a, b) => (agentNameByEmail[a] || a).localeCompare(agentNameByEmail[b] || b));

  const matchesAgentFilter = (o) => {
    if (agentFilter === 'all') return true;
    if (agentFilter === 'none') return !o.agent_email;
    return (o.agent_email || '').toLowerCase() === agentFilter;
  };

  // Bestellungen filters
  const filteredBestellungen = bestellungen.filter(o => {
    const matchSearch = o.lead_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      o.order_number?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatus = statusFilter === 'all' || o.status === statusFilter;
    return matchSearch && matchStatus && matchesAgentFilter(o);
  });

  // Angebote filters  
  const filteredAngebote = angebote.filter(o => {
    const matchSearch = o.lead_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      o.order_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      !searchTerm;
    return matchSearch && matchesAgentFilter(o);
  });

  const openCount = bestellungen.filter(o => o.status === 'Neu').length;
  const inProgressCount = bestellungen.filter(o => o.status === 'In Bearbeitung').length;
  const shippedCount = bestellungen.filter(o => o.status === 'Versendet').length;
  const doneCount = bestellungen.filter(o => o.status === 'Abgeschlossen').length;

  const bestellungenTotal = bestellungen.reduce((s, o) => s + (o.subtotal || 0), 0);
  const angeboteTotal = angebote.reduce((s, o) => s + (o.subtotal || 0), 0);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-20 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <PageHeader
                icon={<ShoppingCart className="w-5 h-5" />}
                title="Aufträge"
                subtitle={`${bestellungen.length} Bestellungen`}
              />
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-24">
        <Tabs defaultValue="bestellungen">
          {/* Tab triggers */}
          <div className="flex items-center justify-between mb-6">
            <TabsList className="bg-slate-100">
              <TabsTrigger value="bestellungen" className="gap-2 data-[state=active]:bg-emerald-600 data-[state=active]:text-white">
                <ShoppingCart className="w-4 h-4" />
                Bestellungen
                {openCount > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold data-[state=active]:bg-white data-[state=active]:text-emerald-600">
                    {openCount}
                  </span>
                )}
              </TabsTrigger>
            </TabsList>
          </div>

          {/* ═══ BESTELLUNGEN TAB ═══ */}
          <TabsContent value="bestellungen">
            {/* KPI row */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
              {[
                { label: 'Neu', value: openCount, color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200' },
                { label: 'In Bearbeitung', value: inProgressCount, color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200' },
                { label: 'Versendet', value: shippedCount, color: 'text-purple-600', bg: 'bg-purple-50', border: 'border-purple-200' },
                { label: 'Abgeschlossen', value: doneCount, color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200' },
              ].map(kpi => (
                <Card key={kpi.label} className={`${kpi.bg} ${kpi.border} border`}>
                  <CardContent className="p-4">
                    <p className={`text-2xl font-bold ${kpi.color}`}>{kpi.value}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{kpi.label}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-3 mb-4">
              <div className="relative flex-1 min-w-[180px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Suchen..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-44">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Status</SelectItem>
                  {Object.keys(ORDER_STATUS_CONFIG).map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={agentFilter} onValueChange={setAgentFilter}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Agent" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Agenten</SelectItem>
                  <SelectItem value="none">Ohne Agent</SelectItem>
                  {agents.map(a => <SelectItem key={a} value={a}>{agentNameByEmail[a] || a}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden space-y-3">
              {filteredBestellungen.map(order => (
                <Card
                  key={order.id}
                  className="p-4 cursor-pointer hover:shadow-md transition-shadow active:scale-[0.99]"
                  onClick={() => setSelectedOrder(order)}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="font-mono text-sm font-medium whitespace-nowrap">{order.order_number}</p>
                      <p className="text-sm font-semibold text-slate-800 mt-0.5">{order.lead_name}</p>
                    </div>
                    <OrderStatusBadge status={order.status} />
                  </div>
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
                    <div className="flex items-center gap-3 text-xs text-slate-500">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5" />
                        {order.created_date && format(new Date(order.created_date), 'dd.MM.yyyy', { locale: de })}
                      </span>
                      <span>{order.items?.length || 0} Pos.</span>
                    </div>
                    <p className="font-bold">€{order.total?.toLocaleString('de-DE', { minimumFractionDigits: 2 })}</p>
                  </div>
                </Card>
              ))}
              {isLoading && filteredBestellungen.length === 0 && (
                <SkeletonCard count={4} />
              )}
              {!isLoading && filteredBestellungen.length === 0 && (
                <EmptyState
                  icon={ShoppingCart}
                  title="Keine Bestellungen"
                  description="Es wurden keine Bestellungen gefunden, die Ihren Filtern entsprechen."
                  compact
                />
              )}
            </div>

            {/* Desktop table */}
            <Card className="hidden md:block overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Nr.</TableHead>
                    <TableHead>Kontakt</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Datum</TableHead>
                    <TableHead>Pos.</TableHead>
                    <TableHead>Agent</TableHead>
                    <TableHead className="text-right">Betrag</TableHead>
                    <TableHead className="text-center">Aktion</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredBestellungen.map(order => (
                    <TableRow
                      key={order.id}
                      className="cursor-pointer hover:bg-slate-50 group"
                      onClick={() => setSelectedOrder(order)}
                    >
                      <TableCell>
                        <span className="font-mono text-sm text-slate-500 whitespace-nowrap">{order.order_number}</span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-slate-400 flex-shrink-0" />
                          <span className="font-medium">{order.lead_name}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <OrderStatusBadge status={order.status} />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5 text-sm text-slate-600">
                          <Calendar className="w-3.5 h-3.5" />
                          {order.created_date && format(new Date(order.created_date), 'dd.MM.yyyy', { locale: de })}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-slate-600">{order.items?.length || 0}</span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5 text-sm text-slate-500">
                          <User className="w-3.5 h-3.5" />
                          <span className="truncate max-w-[140px]">{order.agent_email}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-bold">
                        €{order.total?.toLocaleString('de-DE', { minimumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell className="text-center" onClick={e => e.stopPropagation()}>
                        {order.status === 'Geliefert' && !order.invoice_number && (
                          <Button
                            size="sm"
                            className="bg-cyan-600 hover:bg-cyan-700 text-white gap-1"
                            onClick={() => createInvoiceMutation.mutate(order)}
                            disabled={createInvoiceMutation.isPending}
                          >
                            <FileText className="w-3.5 h-3.5" />
                            Rechnung
                          </Button>
                        )}
                        {/* Fatura Warenausgang'da üretildiyse: sadece Berechnet'e ilerlet */}
                        {order.status === 'Geliefert' && order.invoice_number && (
                          <Button
                            size="sm"
                            className="bg-cyan-600 hover:bg-cyan-700 text-white gap-1"
                            onClick={() => updateStatusMutation.mutate({ id: order.id, status: 'Berechnet' })}
                            disabled={updateStatusMutation.isPending}
                            title={`Rechnung ${order.invoice_number} bereits erstellt`}
                          >
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            Berechnet
                          </Button>
                        )}
                        {order.status === 'Berechnet' && (
                          <Button
                            size="sm"
                            className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1"
                            onClick={() => completeOrderMutation.mutate(order.id)}
                            disabled={completeOrderMutation.isPending}
                          >
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            Abschließen
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {isLoading && filteredBestellungen.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} className="py-4">
                        <SkeletonCard count={3} variant="row" />
                      </TableCell>
                    </TableRow>
                  )}
                  {!isLoading && filteredBestellungen.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8}>
                        <EmptyState
                          icon={ShoppingCart}
                          title="Keine Bestellungen"
                          description="Es wurden keine Bestellungen gefunden, die Ihren Filtern entsprechen."
                          compact
                        />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Card>

            {/* Total */}
            <div className="mt-4 text-right text-sm text-slate-500">
              Gesamtumsatz Bestellungen (Netto):{' '}
              <span className="font-bold text-slate-800">
                €{bestellungenTotal.toLocaleString('de-DE', { minimumFractionDigits: 2 })}
              </span>
            </div>
          </TabsContent>

          {/* ═══ ANGEBOTE TAB ═══ */}
        </Tabs>
      </main>

      {/* ═══ ORDER DETAIL SHEET ═══ */}
      <Sheet open={!!selectedOrder} onOpenChange={() => setSelectedOrder(null)}>
        <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto">
          {selectedOrder && (
            <>
              <SheetHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <SheetTitle className="font-mono">{selectedOrder.order_number}</SheetTitle>
                    <p className="text-slate-500 mt-0.5">{selectedOrder.lead_name}</p>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <StatusBadge status={selectedOrder.order_type} type="order" />
                    {selectedOrder.order_type === 'Bestellung' && (
                      <OrderStatusBadge status={selectedOrder.status} />
                    )}
                  </div>
                </div>
              </SheetHeader>

              <div className="mt-6 space-y-6">
                {/* Meta */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">Erstellt</p>
                    <p className="font-medium">
                      {selectedOrder.created_date && format(new Date(selectedOrder.created_date), 'dd.MM.yyyy HH:mm', { locale: de })}
                    </p>
                  </div>
                  <div>
                    <p className="text-slate-500">Agent</p>
                    <p className="font-medium">{selectedOrder.agent_email}</p>
                  </div>
                </div>

                {/* Status switcher for Bestellungen */}
                {selectedOrder.order_type === 'Bestellung' && (
                  <div>
                    <p className="text-sm font-medium text-slate-700 mb-2">Status aktualisieren</p>
                    <div className="flex flex-wrap gap-2">
                      {Object.keys(ORDER_STATUS_CONFIG).map(s => (
                        <button
                          key={s}
                          onClick={() => updateStatusMutation.mutate({ id: selectedOrder.id, status: s })}
                          disabled={selectedOrder.status === s || updateStatusMutation.isPending}
                          className={`px-3 py-1 rounded-full text-xs font-medium border transition-all
                            ${selectedOrder.status === s
                              ? ORDER_STATUS_CONFIG[s].color + ' ring-2 ring-offset-1 ring-slate-400'
                              : 'bg-white border-slate-200 text-slate-500 hover:border-slate-400'
                            } disabled:opacity-50`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Items */}
                <div>
                  <h3 className="font-semibold mb-3">Positionen</h3>
                  <div className="border rounded-lg divide-y">
                    {(isEditing ? editingItems : selectedOrder.items)?.map((item, i) => {
                      const custPrice = customerPriceMap[item.product_id];
                      const hasCustPrice = custPrice != null && custPrice !== item.unit_price;
                      const desc = item.beschreibung || item.description || '';
                      const unit = item.unit || 'Stk';
                      return (
                        <div key={i} className="p-3 space-y-2">
                          {/* Product name + image — full width, no truncation */}
                          <div className="flex items-start gap-2.5">
                            {item.image_url && (
                              <img loading="lazy" decoding="async" src={item.image_url} alt="" className="w-10 h-10 rounded object-cover flex-shrink-0 border" />
                            )}
                            <div className="flex-1">
                              <p className="font-medium text-sm text-slate-900 leading-snug break-words">{item.product_name}</p>
                              {item.sku && <p className="text-[11px] text-slate-400 font-mono mt-0.5">{item.sku}</p>}
                            </div>
                          </div>

                          {/* Description */}
                          {desc && (
                            <p className="text-xs text-slate-500 leading-relaxed">{desc}</p>
                          )}

                          {/* Customer price hint */}
                          {hasCustPrice && !isEditing && (
                            <div className="flex items-center gap-1">
                              <Tag className="w-3 h-3 text-amber-600" />
                              <span className="text-xs text-amber-600 font-medium">
                                Kundenpreis: €{custPrice?.toFixed(2)}
                              </span>
                            </div>
                          )}

                          {/* Quantity × Price — sits on its own row */}
                          {isEditing ? (
                            <div className="flex items-center gap-2 pt-1">
                              <input
                                type="number"
                                inputMode="numeric"
                                step="1"
                                min="0"
                                className="w-20 text-right h-8 border rounded px-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                                value={item.quantity}
                                onChange={(e) => handleItemChange(i, 'quantity', e.target.value)}
                                onFocus={(e) => e.target.select()}
                              />
                              <span className="text-sm text-slate-500">×</span>
                              <div className="relative">
                                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">€</span>
                                <input
                                  type="number"
                                  inputMode="decimal"
                                  step="0.01"
                                  min="0"
                                  className="w-24 text-right h-8 border rounded pl-5 pr-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                                  value={item.unit_price}
                                  onChange={(e) => handleItemChange(i, 'unit_price', e.target.value)}
                                  onFocus={(e) => e.target.select()}
                                />
                              </div>
                              <span className="ml-auto font-semibold text-sm">
                                €{((item.quantity || 0) * (item.unit_price || 0)).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </span>
                            </div>
                          ) : (
                            <div className="flex items-center justify-between pt-1 border-t border-slate-100">
                              <span className="text-sm text-slate-500">{item.quantity} {unit} × €{item.unit_price?.toFixed(2)}</span>
                              <span className="font-semibold text-sm">€{((item.quantity || 0) * (item.unit_price || 0)).toFixed(2)}</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Totals */}
                <Card className="p-4 bg-slate-50">
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Zwischensumme</span>
                      <span>€{liveSubtotal.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                    <div className="flex justify-between text-sm text-slate-500">
                      <span>MwSt. ({liveTaxRate}%)</span>
                      <span>€{liveTaxAmount.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                    <div className="flex justify-between font-bold text-lg pt-2 border-t">
                      <span>Gesamt</span>
                      <span>€{liveTotal.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                  </div>
                </Card>

                {selectedOrder.notes && (
                  <div>
                    <h3 className="font-semibold mb-2">Notizen</h3>
                    <p className="text-slate-600">{selectedOrder.notes}</p>
                  </div>
                )}

                {/* Action buttons */}
                <div className="flex gap-3 mt-6">
                  {isEditing ? (
                    <>
                      <Button variant="outline" className="flex-1" onClick={() => setIsEditing(false)}>
                        Abbrechen
                      </Button>
                      <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700" onClick={handleApproveAndDownload}>
                        Speichern & Genehmigen
                      </Button>
                    </>
                  ) : (
                    <>
                      {selectedOrder.order_type === 'Angebot' && (
                        <Button className="flex-1" onClick={() => setIsEditing(true)}>
                          <Edit className="w-4 h-4 mr-2" />
                          Bearbeiten & Genehmigen
                        </Button>
                      )}

                      {/* Angebot → Bestellung: tek ve bilinçli dönüşüm yolu.
                          Yalnızca onaylı teklifte görünür. */}
                      {selectedOrder.order_type === 'Angebot'
                        && selectedOrder.order_status === 'approved' && (
                        <Button
                          className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                          onClick={() => {
                            if (window.confirm(
                              `Angebot ${selectedOrder.order_number} in eine Bestellung umwandeln?\n` +
                              'Der Auftrag erscheint danach im Warenausgang und der Bestand wird reserviert.'
                            )) {
                              convertToBestellungMutation.mutate(selectedOrder);
                            }
                          }}
                          disabled={convertToBestellungMutation.isPending}
                        >
                          <ShoppingCart className="w-4 h-4 mr-2" />
                          In Bestellung umwandeln
                        </Button>
                      )}

                      {kannZuschlagErlassen(selectedOrder) && (
                        <Button
                          variant="outline"
                          className="flex-1"
                          onClick={() => {
                            if (window.confirm('Mindermengenzuschlag für diese Bestellung erlassen? Die Position wird entfernt und die Summen neu berechnet.')) {
                              erlasseZuschlagMutation.mutate(selectedOrder);
                            }
                          }}
                          disabled={erlasseZuschlagMutation.isPending}
                        >
                          Zuschlag erlassen
                        </Button>
                      )}

                      {/* Rechnung erstellen — Geliefert sonrası veya geriye
                          dönük olarak Abgeschlossen üzerinde. */}
                      {selectedOrder.order_type === 'Bestellung'
                        && (selectedOrder.status === 'Geliefert'
                            || (selectedOrder.status === 'Abgeschlossen' && !selectedOrder.invoice_number)) && (
                        <Button
                          className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white"
                          onClick={() => createInvoiceMutation.mutate(selectedOrder)}
                          disabled={createInvoiceMutation.isPending}
                        >
                          <FileText className="w-4 h-4 mr-2" />
                          Rechnung erstellen
                        </Button>
                      )}

                      {/* Abschließen — sadece Berechnet statüsünde, ödeme tamam */}
                      {selectedOrder.order_type === 'Bestellung'
                        && selectedOrder.status === 'Berechnet' && (
                        <Button
                          className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                          onClick={() => completeOrderMutation.mutate(selectedOrder.id)}
                          disabled={completeOrderMutation.isPending}
                        >
                          <CheckCircle2 className="w-4 h-4 mr-2" />
                          Abschließen (Zahlung)
                        </Button>
                      )}

                      {/* Versand über Shopify: automatisch beim Anlegen; hier
                          Nachsenden bei Fehlern oder für ältere Bestellungen. */}
                      {selectedOrder.order_type === 'Bestellung'
                        && selectedOrder.status !== 'Storniert'
                        && !['shopify', 'ebay'].includes((selectedOrder.source || '').toLowerCase()) && (
                        isInShopify(selectedOrder) ? (
                          <span className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
                            <CheckCircle2 className="w-4 h-4" />
                            In Shopify
                          </span>
                        ) : istEigeneZustellung(selectedOrder, eigeneZustellung) ? (
                          <Button
                            variant="outline"
                            className="flex-1"
                            title="Kunde ist auf eigene Zustellung gestellt — nur bewusst senden"
                            onClick={() => {
                              if (!window.confirm(`${selectedOrder.order_number}: Dieser Kunde wird selbst beliefert.\nTrotzdem an Shopify senden? DPD erstellt dann ein Versandetikett.`)) return;
                              pushOrderInBackground(selectedOrder.id, {
                                orderNumber: selectedOrder.order_number,
                                force: true,
                                onDone: () => queryClient.invalidateQueries(['admin-all-orders']),
                              });
                            }}
                          >
                            <Send className="w-4 h-4 mr-2" />
                            Eigene Zustellung · trotzdem senden
                          </Button>
                        ) : (
                          <Button
                            variant="outline"
                            className="flex-1"
                            onClick={() => pushOrderInBackground(selectedOrder.id, {
                              orderNumber: selectedOrder.order_number,
                              onDone: () => queryClient.invalidateQueries(['admin-all-orders']),
                            })}
                          >
                            <Send className="w-4 h-4 mr-2" />
                            An Shopify senden
                          </Button>
                        )
                      )}

                      <Button
                        variant="outline"
                        className="flex-1"
                        onClick={() => downloadOrderPDF(selectedOrder)}
                      >
                        <FileText className="w-4 h-4 mr-2" />
                        PDF
                      </Button>

                      {/* Delete button for Admin */}
                      <Button
                        variant="outline"
                        size="icon"
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 border-red-200"
                        onClick={() => {
                          if (window.confirm(`"${selectedOrder.order_number}" wirklich löschen?`)) {
                            deleteOrderMutation.mutate(selectedOrder.id);
                          }
                        }}
                        disabled={deleteOrderMutation.isPending}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>

                      {selectedOrder.pdf_url && (
                        <Button variant="ghost" size="icon" asChild>
                          <a href={selectedOrder.pdf_url} target="_blank" rel="noopener noreferrer">
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
